/**
 * useReferralCapture — listens for `?ref=<code>` on first load. If found:
 *   1. Stores the code in localStorage (`cca_outlet_ref`) so it survives
 *      across sessions and auth provider redirects (Google/Facebook/Apple
 *      OAuth bounces strip query params from the landing URL).
 *   2. Pings POST /api/referrals/visit anonymously (anonymous visit count).
 *   3. Strips `?ref=` from the URL so it doesn't leak into share/copy
 *      actions or get re-counted on back-button navigation.
 *
 * Separately, useReferralAttribution — runs whenever the user becomes
 * authenticated. If a stored ref code exists, POSTs to
 * /api/referrals/attribute (auth required), which first-touches the
 * user's record. Clears localStorage afterwards so re-login attempts
 * with a different `?ref=` don't reassign credit.
 *
 * First-touch attribution model — once attributed, the code is locked
 * on the user record server-side. Subsequent ref clicks don't overwrite.
 */
import { useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'cca_outlet_ref';
const STORAGE_TS_KEY = 'cca_outlet_ref_ts';
const MAX_AGE_DAYS = 90; // forget ref after 3 months

function readStoredRef() {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || '0', 10);
    if (!code) return null;
    if (Date.now() - ts > MAX_AGE_DAYS * 24 * 3600 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_TS_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

function clearStoredRef() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TS_KEY);
  } catch { /* ignore */ }
}

export function useReferralCapture() {
  useEffect(() => {
    if (typeof window === 'undefined' || !API) return;
    try {
      const url = new URL(window.location.href);
      // Accept either `?ref=<code>` (our canonical) or `?utm_source=<code>`
      // as an alias — some email senders / news aggregators auto-strip
      // non-`utm_*` params, so the alias keeps attribution intact when
      // pitches go through those gateways.
      const ref = url.searchParams.get('ref') || url.searchParams.get('utm_source');
      if (!ref) return;

      // Sanitize: same regex as backend (lowercase alnum + hyphen, 2-40).
      const code = ref.toLowerCase().trim();
      if (!/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(code)) return;

      localStorage.setItem(STORAGE_KEY, code);
      localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));

      // Fire and forget — don't block UX on the analytics ping.
      fetch(`${API}/api/referrals/visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          code,
          route: window.location.pathname.slice(0, 200),
          session_id: null,
        }),
      }).catch(() => { /* silent */ });

      // Strip both `?ref=` and `?utm_source=` from the URL without
      // reloading. Leaves any other query params intact so we don't
      // clobber legitimate state.
      url.searchParams.delete('ref');
      url.searchParams.delete('utm_source');
      const cleanUrl = url.pathname
        + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '')
        + url.hash;
      window.history.replaceState({}, '', cleanUrl);
    } catch {
      /* never throw from analytics */
    }
  }, []);
}

/**
 * Call once a user becomes authenticated. Posts the stored ref code to
 * /api/referrals/attribute. Idempotent server-side (first-touch).
 */
export async function attributeReferralIfAny(token) {
  if (typeof window === 'undefined' || !API) return;
  const code = readStoredRef();
  if (!code || !token) return;
  try {
    await fetch(`${API}/api/referrals/attribute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ code }),
    });
  } catch {
    /* silent */
  } finally {
    // Even on failure, clear so we don't retry indefinitely on every
    // session refresh.
    clearStoredRef();
  }
}

export default useReferralCapture;
